import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase-config';

const Faculty = () => {
  const [faculty, setFaculty] = useState([]);
  const [filter, setFilter] = useState('all');
  const [stats, setStats] = useState({ total: 0, governing: 0, teaching: 0 });

  useEffect(() => {
    const loadFaculty = async () => {
      try {
        const q = query(collection(db, 'faculty'), orderBy('priority', 'asc'));
        const snapshot = await getDocs(q);
        const data = [];
        snapshot.forEach(doc => data.push({ id: doc.id, ...doc.data() }));
        setFaculty(data);

        const governing = data.filter(f => f.category === 'Governing Body').length;
        const teaching = data.filter(f => f.category === 'Teaching').length;
        setStats({ total: data.length, governing, teaching });
      } catch (error) {
        console.error('Error loading faculty:', error);
      }
    };
    loadFaculty();
  }, []);

  const categories = ['all', ...new Set(faculty.map(f => f.category).filter(Boolean))];
  const filteredFaculty = filter === 'all' ? faculty : faculty.filter(f => f.category === filter);

  return (
    <div className="container" style={{ paddingTop: '40px' }}>
      <motion.h1
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        style={{ fontFamily: "'Playfair Display', serif", fontSize: '2.5rem', marginBottom: '10px' }}
      >
        <i className="fas fa-chalkboard-user" style={{ color: 'var(--clr-blue)' }}></i> Our Faculty
      </motion.h1>
      <p style={{ color: 'var(--text-secondary)', marginBottom: '30px' }}>Meet the brilliant minds behind our medical education</p>

      {/* Stats */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '20px',
        marginBottom: '40px'
      }}>
        <div style={{
          background: 'rgba(255, 255, 255, 0.5)',
          backdropFilter: 'blur(20px) saturate(140%)',
          borderRadius: '16px',
          padding: '24px',
          textAlign: 'center',
          borderBottom: '3px solid var(--clr-blue)'
        }}>
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: '2rem', fontWeight: 800, color: 'var(--clr-blue)' }}>{stats.total}</div>
          <div style={{ fontSize: '0.85rem', fontWeight: 700, marginTop: '5px' }}>Total Faculty</div>
        </div>
        <div style={{
          background: 'rgba(255, 255, 255, 0.5)',
          backdropFilter: 'blur(20px) saturate(140%)',
          borderRadius: '16px',
          padding: '24px',
          textAlign: 'center',
          borderBottom: '3px solid var(--clr-purple)'
        }}>
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: '2rem', fontWeight: 800, color: 'var(--clr-purple)' }}>{stats.governing}</div>
          <div style={{ fontSize: '0.85rem', fontWeight: 700, marginTop: '5px' }}>Governing Body</div>
        </div>
        <div style={{
          background: 'rgba(255, 255, 255, 0.5)',
          backdropFilter: 'blur(20px) saturate(140%)',
          borderRadius: '16px',
          padding: '24px',
          textAlign: 'center',
          borderBottom: '3px solid var(--clr-emerald)'
        }}>
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: '2rem', fontWeight: 800, color: 'var(--clr-emerald)' }}>{stats.teaching}</div>
          <div style={{ fontSize: '0.85rem', fontWeight: 700, marginTop: '5px' }}>Teaching Faculty</div>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '30px', justifyContent: 'center' }}>
        {categories.map(cat => (
          <button
            key={cat}
            onClick={() => setFilter(cat)}
            style={{
              padding: '14px 32px',
              background: filter === cat ? 'linear-gradient(135deg, var(--clr-blue), #0284c7)' : 'rgba(255, 255, 255, 0.4)',
              backdropFilter: 'blur(20px) saturate(140%)',
              border: filter === cat ? 'none' : '1px solid var(--clr-blue-border)',
              borderRadius: '50px',
              color: filter === cat ? 'white' : 'var(--text-secondary)',
              fontWeight: 700,
              fontSize: '1rem',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}
          >
            <i className={`fas ${cat === 'all' ? 'fa-users' : cat === 'Governing Body' ? 'fa-landmark' : cat === 'Teaching' ? 'fa-chalkboard-user' : 'fa-building-user'}`}></i>
            {cat === 'all' ? 'All Faculty' : cat}
          </button>
        ))}
      </div>

      {/* Faculty Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
        gap: '28px',
        marginBottom: '60px'
      }}>
        {filteredFaculty.length === 0 ? (
          <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '60px', background: 'rgba(255,255,255,0.5)', borderRadius: '24px' }}>
            <i className="fas fa-user-slash" style={{ fontSize: '2rem', display: 'block', marginBottom: '10px' }}></i>
            <p>No faculty members found in this category</p>
          </div>
        ) : (
          filteredFaculty.map((item, index) => {
            const colors = {
              'Governing Body': 'var(--clr-purple)',
              'Teaching': 'var(--clr-blue)',
              'Administration': 'var(--clr-emerald)'
            };
            const color = colors[item.category] || 'var(--clr-blue)';
            const icons = {
              'Governing Body': 'fa-landmark',
              'Teaching': 'fa-chalkboard-user',
              'Administration': 'fa-building-user'
            };
            const icon = icons[item.category] || 'fa-user-tie';

            return (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                style={{
                  background: 'rgba(255, 255, 255, 0.5)',
                  backdropFilter: 'blur(20px) saturate(140%)',
                  borderRadius: '24px',
                  overflow: 'hidden',
                  border: '1px solid var(--clr-blue-border)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
                whileHover={{ y: -8, boxShadow: '0 12px 32px rgba(0, 0, 0, 0.1)' }}
              >
                <Link to={`/faculty/${item.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                  <div style={{
                    position: 'relative',
                    height: '280px',
                    overflow: 'hidden',
                    background: 'linear-gradient(135deg, #0f172a, #1e293b)'
                  }}>
                    <img
                      src={item.image || 'https://randomuser.me/api/portraits/men/32.jpg'}
                      alt={item.name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.5s' }}
                    />
                    <div style={{
                      position: 'absolute',
                      top: '15px',
                      right: '15px',
                      padding: '6px 14px',
                      borderRadius: '30px',
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      background: color,
                      color: 'white'
                    }}>
                      <i className={`fas ${icon}`}></i> {item.category || 'Faculty'}
                    </div>
                  </div>
                  <div style={{ padding: '22px' }}>
                    <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.2rem', marginBottom: '5px' }}>{item.name || 'Faculty Name'}</h3>
                    <p style={{ color: 'var(--clr-blue)', fontSize: '0.85rem', fontWeight: 600, marginBottom: '8px' }}>{item.designation || 'Professor'}</p>
                    <span style={{
                      display: 'inline-block',
                      padding: '4px 12px',
                      background: 'rgba(14,165,233,0.1)',
                      borderRadius: '20px',
                      fontSize: '0.7rem',
                      color: 'var(--clr-blue)',
                      fontWeight: 600
                    }}>{item.department || 'Medical Sciences'}</span>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '12px', paddingTop: '12px', borderTop: '1px dashed rgba(14,165,233,0.2)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <i className="fas fa-user-graduate" style={{ color: 'var(--clr-blue)' }}></i>
                      <span>Expert in {item.department?.split(' ')[0] || 'Medical'} Sciences</span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default Faculty;